import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, Col, Form, Label, Modal, ModalBody, Row } from "reactstrap";

import defaultTrxImg from "@/assets/images/default_transaction.png";
import { Badge } from "@/components/Common/Badge";
import { ApproveButton, CancelButton, RejectButton, ReturnButton } from "@/components/Common/Buttons";
import { DateTimeLabel } from "@/components/Common/DateTimeLabel";
import { DatePickerField, InputField } from "@/components/Common/Inputs";
import { CurrencyInputField } from "@/components/Common/Inputs/CurrencyInputField";
import RadioButtonGroup from "@/components/Common/Inputs/RadioButtonGroup";
import { PhotoViewer } from "@/components/Common/PhotoViewer";
import SwitchButton from "@/components/Common/SwitchButton";
import useCustomHook from "@/components/Hooks/useCustomHook";

import ActionModal from "./ActionModal";
import History from "./History";

import { useFindCollectionsQuery } from "@/api/Endpoints/FMS/Transactions/Collection/Collections";

const profileBg = require(`@/${process.env.REACT_APP_CUSTOMER_USER_PROFILE}`);

const ViewDetails = ({ data, show = false, onCloseClick, parentKey, onUpdateParentKey }) => {
  const { state, customFunction } = useCustomHook();
  const { data: transactionData, refetch } = useFindCollectionsQuery(
    { collectionId: data?.collectionId },
    { skip: !data?.collectionId, refetchOnMountOrArgChange: show }
  );
  const findData = transactionData?.returnData;
  const { reset, watch, control } = useForm({ ...findData });

  // Use Effect to handle the data using state
  useEffect(() => {
    reset({
      ...findData,
    });
    customFunction.updateState({
      data: {
        ...state,
        data,
        ...findData,
      },
    });
  }, [show, data, findData, refetch, reset]);

  // Handle returnData

  const handleActions = (bool, data) => {
    if (bool) {
      customFunction.updateToggle("toggleAction");
      customFunction.updateToggle("refetchHistory");
      onUpdateParentKey(parentKey + 1);
    } else {
      customFunction.updateToggle("toggleAction", { ...findData, actionName: data });
    }
  };

  const paymentTypeId = watch("paymentTypeId");
  const statusName = watch("statusName");

  return (
    <React.Fragment>
      <ActionModal
        state={state}
        customFunction={customFunction}
        collectionId={data?.collectionId}
        data={state?.trxValue}
        show={state.toggle.toggleAction}
        onCloseClick={handleActions}
      />

      <Modal
        className="modal-fullscreen"
        modalClassName="flip"
        id="requestDetails"
        size="xl"
        isOpen={show}
        toggle={onCloseClick}
        centered
        backdrop="static">
        <ModalBody>
          <div>
            <div className="profile-foreground position-relative mx-n4 mt-n4">
              <div className="profile-wid-bg" >
                <picture>
                  <source srcSet={profileBg} type="image/jpeg" />
                  <img src={profileBg} alt="" className="profile-wid-img" />
                </picture>
              </div>
              <button
                type="button"
                className=" btn btn-icon btn-sm btn-ghost-danger fs-16 position-absolute top-0 end-0 m-3 d-lg-none "
                style={{ zIndex: 1055 }}
                onClick={onCloseClick}
              >
                X
              </button>
            </div>
            <div className="pt-4 mb-4 mb-lg-3 pb-lg-4">
              <Row className="g-4">
                <div className="col-md-auto">
                  <PhotoViewer>
                    <img src={defaultTrxImg} alt="" className="" style={{ width: "100px", height: "80px", cursor: "pointer" }} />
                  </PhotoViewer>
                </div>

                <Col style={{ paddingLeft: "0px" }}>
                  <div>
                    <h3 className="text-white mb-1">Collection Request Details</h3>
                    <p className="text-white text-opacity-75">
                      Date {state.data?.statusName}:{" "}
                      <span className="fw-semibold">{<DateTimeLabel value={state.data?.statusDate} /> || "Draft"}</span>{" "}
                      <Badge value={state.data?.statusName} color={state.data?.statusColor} />
                      <br />
                      Reference No. : <span className="fs-16 fw-semibold" style={{ color: '#0ab39c' }}>{findData?.referenceNo}</span>
                    </p>
                  </div>
                </Col>

                <Col
                  xs={12}
                  lg="auto"
                  className="order-last order-lg-0 d-none d-lg-flex align-items-start"
                >
                  <button
                    type="button"
                    className="btn btn-icon btn-sm btn-ghost-danger fs-16 mt-4 mt-lg-0"
                    onClick={onCloseClick}
                  >
                    X
                  </button>
                </Col>
              </Row>
            </div>

            <Row>
              <Col lg={12}>
                <div>
                  <Row>
                    <Col xxl={12}>
                      <Card>
                        <CardBody>
                          <Row>
                            <Col lg={6}>
                              <Card>
                                <CardBody>
                                  <Row className="align-items-center gy-2">
                                    <Col lg={8} xs={12}>
                                      <h5 className="card-title mb-0 text-center text-lg-start">
                                        Request Details
                                      </h5>
                                    </Col>

                                    <Col lg={4} xs={12}>
                                      <div className=" d-flex justify-content-center justify-content-lg-end gap-1 mt-2 mt-lg-0 ">
                                        <ReturnButton
                                          isShowButton={state.data?.actions?.return}
                                          onClick={() => handleActions(false, "Return")}
                                        />
                                        <ApproveButton
                                          isShowButton={state.data?.actions?.approve}
                                          onClick={() => handleActions(false, "Approve")}
                                        />
                                        <RejectButton
                                          isShowButton={state.data?.actions?.reject}
                                          onClick={() => handleActions(false, "Reject")}
                                        />
                                        <CancelButton
                                          isShowButton={state.data?.actions?.cancel}
                                          onClick={() => handleActions(false, "Cancel")}
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                  <br />
                                  <Row className="mt-2">
                                    <Col xl={12} lg={12}>
                                      <Card>
                                        <CardBody className="border border-dashed border-end-0 border-start-0">
                                          <Form>
                                            <Row className={"mt-10"}>
                                              <Col lg={5}>
                                                <DatePickerField control={control} name="transactionDate" label="Transaction Date" disabled />
                                              </Col>
                                              <Col lg={5}>
                                                <InputField name="orderOfPaymentNo" control={control} label="Order of payment" type="text" disabled />
                                              </Col>
                                              <Col lg={2}>
                                                <div className="mb-2">With OOP</div>
                                                <SwitchButton name="withOOP" control={control} disabled />
                                              </Col>

                                              <Col lg={6}>
                                                <InputField name="clientTypeName" control={control} label="Client Type" type="text" disabled />
                                              </Col>
                                              <Col lg={6}>
                                                <InputField name="clientName" control={control} label="Payor" type="text" disabled />
                                              </Col>
                                              <Col lg={6}>
                                                <InputField name="contactPerson" control={control} label="Contact Person" type="text" disabled />
                                              </Col>
                                              <Col lg={6}>
                                                <CurrencyInputField
                                                  name="collectedAmount"
                                                  control={control}
                                                  label="Total Amount"
                                                  type="text"
                                                  disabled
                                                />
                                              </Col>
                                              <Col lg={6}>
                                                <InputField
                                                  name="transactionTypeName"
                                                  control={control}
                                                  label="Transaction Type"
                                                  type="text"
                                                  disabled
                                                />
                                              </Col>
                                              <Col lg={12}>
                                                <Label className="form-label">Payment Type</Label>
                                                <RadioButtonGroup
                                                  name="paymentTypeId"
                                                  disabled
                                                  control={control}
                                                  options={[
                                                    { value: 1, label: "Cash" },
                                                    {
                                                      value: 2,
                                                      label: "Check",
                                                    },
                                                  ]}
                                                />
                                              </Col>
                                            </Row>

                                            {paymentTypeId === 2 && (
                                              <Row>
                                                <Col lg={12}>
                                                  <InputField name="checkBankName" control={control} label="Bank Name" type="text" disabled />
                                                </Col>
                                                <Col lg={6}>
                                                  <InputField name="checkNo" control={control} label="Check Number" type="text" disabled />
                                                </Col>
                                                <Col lg={6}>
                                                  <DatePickerField name="checkDate" control={control} label="Check Date" type="text" disabled />
                                                </Col>
                                              </Row>
                                            )}
                                            <Row>
                                              <Col lg={12}>
                                                <InputField disabled name="remarks" control={control} label="Remarks" type="textarea" rows={5} />
                                              </Col>

                                              <Col lg={6}>
                                                <DatePickerField control={control} name="dateSubmitted" label="Date Submitted" disabled />
                                              </Col>
                                              <Col lg={6}>
                                                <InputField name="submittedByName" control={control} label="Submitted By Name" type="text" disabled />
                                              </Col>
                                            </Row>

                                            <Row className={"mt-10"}>
                                              {statusName !== "Submitted" ? (
                                                <>
                                                  <Col lg={6}>
                                                    <InputField
                                                      name="statusDate"
                                                      control={control}
                                                      label={`Date ${findData?.statusName}`}
                                                      type="text"
                                                      disabled
                                                    />
                                                  </Col>

                                                  <Col lg={6}>
                                                    <InputField
                                                      name="statusByName"
                                                      control={control}
                                                      label={`${findData?.statusName} By`}
                                                      type="text"
                                                      disabled
                                                    />
                                                  </Col>
                                                </>
                                              ) : null}
                                            </Row>
                                          </Form>
                                        </CardBody>
                                      </Card>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            </Col>

                            <Col lg={6}>
                              <History key={state.toggle.refecthHistory} parentId={data?.collectionId} />
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ViewDetails;
